import { Button, LoaderNew } from "@brightagency/headstart-common";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { Formik, Form, Field, ErrorMessage } from "formik";
import React, { useEffect, useRef } from "react";
import { RichText } from "../../atoms/RichText";
import { Separator } from "../../atoms/Separator";
import { useGravityForm } from "./service";
import {
    FieldWrapper,
    CheckboxWrapper,
    RadioWrapper,
    Label,
    Error,
    ServerError,
    Description,
    ListButton,
    ListColumn,
    ListRow,
    SubmitButtonWrapper,
    RadioControl,
    FieldSet,
    CheckboxControl,
    ListButtons,
    FormSection,
    LoaderStyleOverride,
} from "./styled";
import { FieldType, ListItemsType } from "./types";

interface Props {
    formId: string;
    title?: string;
    variableReplacements?: {
        [key: string]: string;
    };
    submitButtonAlignment?: "center" | "left";
    autoFocusFirstField?: boolean;
}

type Validator = (value: string) => string | undefined;

const validationFunctions: { [key: string]: Validator } = {
    required: (value: string) => (value ? undefined : "This field is required"),
    email: (value: string) => {
        const emailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
        if (emailRegex.test(value)) {
            return undefined;
        }
        return "Invalid email address";
    },
};

const creatableValidationFunctions = {
    maxLength: (length: number): Validator => (value: string) => {
        if (length >= value.trim().length) {
            return undefined;
        }
        return `Maximum character limit: ${length}`;
    },
};

const getHiddenFieldValue = (field: FieldType): string => {
    switch (field.inputName) {
        case "page_url":
            return window.location.href;
        default:
            return "";
    }
};

const replaceWithVariables = (
    inputString: string,
    variableReplacements: { [key: string]: string },
): string => {
    let output = inputString;

    Object.keys(variableReplacements).forEach(variable => {
        const replacement = variableReplacements[variable];
        output = output.replace(variable, replacement);
    });

    return output;
};

export const GravityForm: React.FC<Props> = props => {
    const {
        formData,
        initError,
        submitForm,
        submitError,
        submitResponse,
        initLoading,
    } = useGravityForm(props.formId);

    const [listInputValues, setListInputValues] = React.useState<ListItemsType>({});
    const inputRefs = useRef<HTMLInputElement[]>([]);

    useEffect(() => {
        if (formData && JSON.stringify(listInputValues) === "{}") {
            const newCounts: ListItemsType = [];
            formData.fields.forEach(field => {
                if (field.type === "list") {
                    newCounts[field.id] = [[...Array((field.choices || [""]).length).fill("")]];
                }
            });
            setListInputValues(newCounts);
        }
    }, [formData, listInputValues, setListInputValues]);

    useEffect(() => {
        if (props.autoFocusFirstField && !initLoading) {
            inputRefs.current?.[0]?.focus();
            inputRefs.current?.[0]?.blur();
        }
    }, [initLoading, props.autoFocusFirstField]);

    const addListInputRow = (field: FieldType) => {
        setListInputValues(items => {
            const next = { ...items };
            next[field.id] = [
                ...next[field.id],
                [...Array((field.choices || [""]).length).fill("")],
            ];
            return next;
        });
    };

    const removeListInputRow = (field: FieldType, rowIndex: number) => {
        setListInputValues(items => {
            const nextValue = {
                ...items,
            };
            nextValue[field.id].splice(rowIndex, 1);
            return nextValue;
        });
    };

    const createListInputOnChange = (field: FieldType, rowIndex: number, columnIndex: number) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = {
                ...listInputValues,
            };
            nextValue[field.id][rowIndex][columnIndex] = event.target.value;
            setListInputValues(nextValue);
        };
    };

    const renderInput = (field: FieldType) => {
        const validations: Validator[] = [];

        if (field.isRequired) {
            validations.push(validationFunctions.required);
        }

        if (field.type === "email") {
            validations.push(validationFunctions.email);
        }

        if (field.maxLength !== "") {
            validations.push(creatableValidationFunctions.maxLength(field.maxLength));
        }

        const commonProps: {
            name: string;
            id: string;
            validate: Validator | undefined;
            placeholder: string;
            innerRef: (ref: HTMLInputElement) => void;
        } = {
            innerRef: inputRef => inputRefs.current.push(inputRef),
            name: `${field.id}`,
            id: `input_${field.id}`,
            validate: undefined,
            placeholder: replaceWithVariables(field.placeholder, props.variableReplacements ?? {}),
        };

        commonProps.validate = (value: string) => {
            let error: undefined | string = undefined;
            validations.forEach(validator => {
                if (!error) {
                    error = validator(value);
                }
            });
            return error;
        };

        switch (field.type) {
            case "html":
                return <RichText content={field.content} />;
            case "section":
                return (
                    <FormSection>
                        <Separator excludeSectionPadding />
                        <h4>{field.label}</h4>
                    </FormSection>
                );
            case "text":
            case "email":
            case "phone":
            case "hidden":
                return <Field {...commonProps} />;
            case "textarea":
                return <Field as="textarea" {...commonProps} />;
            case "select":
                return (
                    <Field {...commonProps} as="select">
                        {(field.choices || []).map((choice, index) => (
                            <option
                                key={index}
                                value={choice.value}
                                defaultChecked={choice.isSelected}
                            >
                                {choice.text}
                            </option>
                        ))}
                    </Field>
                );
            case "radio":
                return (
                    <FieldSet>
                        {(field.choices || []).map((input, index) => (
                            <RadioWrapper key={index}>
                                <Field {...commonProps} value={input.value} type="radio" />
                                <RadioControl />
                                {input.text}
                            </RadioWrapper>
                        ))}
                    </FieldSet>
                );
            case "checkbox":
                return (
                    <FieldSet>
                        {(field.choices || []).map((input, index) => (
                            <CheckboxWrapper key={index}>
                                <Field {...commonProps} value={input.value} type="checkbox" />
                                <CheckboxControl />
                                {input.text}
                            </CheckboxWrapper>
                        ))}
                    </FieldSet>
                );
            case "list": {
                const emptyChoice = { text: "", value: "", isSelected: false };
                return (
                    <fieldset>
                        {(listInputValues[field.id] || []).map((_, rowIndex) => (
                            <ListRow key={rowIndex}>
                                {(field.choices || [emptyChoice]).map((choice, colIndex) => {
                                    const value = listInputValues[field.id][rowIndex][colIndex];
                                    const onChange = createListInputOnChange(
                                        field,
                                        rowIndex,
                                        colIndex,
                                    );
                                    return (
                                        <ListColumn key={colIndex}>
                                            {choice.text && (
                                                <Label
                                                    style={{
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {choice.text}
                                                </Label>
                                            )}
                                            <input
                                                name={`${commonProps.name}[${colIndex}]`}
                                                id={`${commonProps.id}[${colIndex}]`}
                                                type="text"
                                                value={value}
                                                onChange={onChange}
                                            />
                                        </ListColumn>
                                    );
                                })}
                                <ListButtons>
                                    <ListButton
                                        type="button"
                                        onClick={() => addListInputRow(field)}
                                    >
                                        <AddRoundedIcon fontSize={"small"} />
                                    </ListButton>
                                    <ListButton
                                        style={{
                                            opacity:
                                                (listInputValues[field.id] || []).length > 1
                                                    ? 1
                                                    : 0.5,
                                        }}
                                        disabled={!(listInputValues[field.id] || [].length > 1)}
                                        type="button"
                                        onClick={() => {
                                            if ((listInputValues[field.id] || []).length > 1) {
                                                removeListInputRow(field, rowIndex);
                                            }
                                        }}
                                    >
                                        <RemoveRoundedIcon fontSize={"small"} />
                                    </ListButton>
                                </ListButtons>
                            </ListRow>
                        ))}
                    </fieldset>
                );
            }
            default:
                return <p>Unknown input</p>;
        }
    };

    if (initError) {
        return <div>{JSON.stringify(initError)}</div>;
    }

    const formatServerError = (error: { validation_messages: Record<string, string> }) => {
        const errors: React.ReactNode[] = [];
        Object.keys(error.validation_messages).map(key => {
            const value = error.validation_messages[key];
            errors.push(
                <p>
                    <strong>{formData?.fields.find(f => f.id === Number(key))?.label}:</strong>{" "}
                    {value}
                </p>,
            );
        });
        return errors;
    };

    return (
        <div>
            {props.title && <h2>{props.title}</h2>}
            {submitResponse ? (
                <div style={{ margin: "24px 0" }}>
                    <RichText content={submitResponse.confirmation_message} />
                </div>
            ) : formData ? (
                <Formik
                    onSubmit={async (values, formikBag) => {
                        const response = await submitForm(values, listInputValues);
                        if (!response?.is_valid) {
                            formikBag.setErrors(response?.validation_messages || {});
                        }
                    }}
                    initialValues={formData.fields.reduce<{
                        [key: string]: string;
                    }>(
                        (total, current) => ({
                            ...total,
                            [`${current.id}`]: getHiddenFieldValue(current),
                        }),
                        {},
                    )}
                >
                    <Form>
                        {formData.fields.map(field => (
                            <FieldWrapper key={field.id} isHidden={field.type === "hidden"}>
                                {field.type !== "html" &&
                                    field.type !== "section" &&
                                    field.labelPlacement !== "hidden_label" && (
                                        <Label htmlFor={`input_${field.id}`}>{field.label}</Label>
                                    )}
                                {field.description && (
                                    <Description>{field.description}</Description>
                                )}
                                <div>{renderInput(field)}</div>
                                <ErrorMessage component={Error} name={`${field.id}`} />
                            </FieldWrapper>
                        ))}
                        <SubmitButtonWrapper alignment={props.submitButtonAlignment ?? "left"}>
                            <Button appearance="primary" type="submit" maxWidth={-1}>
                                Submit
                            </Button>
                        </SubmitButtonWrapper>
                        {submitError && (
                            <ServerError>
                                Error while submitting:
                                <br />
                                {formatServerError(
                                    submitError || {
                                        validation_messages: {},
                                    },
                                )}
                            </ServerError>
                        )}
                    </Form>
                </Formik>
            ) : (
                <LoaderStyleOverride>
                    <LoaderNew />
                </LoaderStyleOverride>
            )}
        </div>
    );
};
